/**
 * 认证上下文
 * 提供全局 auth 状态管理：token 存储、用户信息、登录/退出
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import type { LoginResponse } from './api'

// ============ 类型 ============

export interface AuthUser {
  token: string
  userId: string
  name: string
  role: 'admin' | 'medical' | 'user'
  hospitalId?: string
  hospitalName?: string
  medicalRole?: string
}

interface AuthContextType {
  user: AuthUser | null
  login: (resp: LoginResponse) => void
  logout: () => void
  syncUser: (user: AuthUser | null) => void
  isAdmin: boolean
  isMedical: boolean
  isUser: boolean
}

// ============ 常量 ============

const STORAGE_KEY = 'auth_user'

// ============ Context ============

const AuthContext = createContext<AuthContextType | null>(null)

// ============ Provider ============

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const login = useCallback((resp: LoginResponse) => {
    const { account } = resp
    // 判断实际角色：有医护记录则为医护，否则用账号角色
    const actualRole: AuthUser['role'] = account.medical_role
      ? 'medical'
      : account.role as AuthUser['role']

    const authUser: AuthUser = {
      token: resp.token,
      userId: account.id,
      name: account.name,
      role: actualRole,
      hospitalId: account.hospital_id,
      hospitalName: account.hospital_name,
      medicalRole: account.medical_role,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authUser))
    localStorage.setItem('token', resp.token)
    setUserState(authUser)
  }, [])

  // 外部可直接调用此方法同步 auth 状态（如小程序webview返回时）
  const syncUser = useCallback((u: AuthUser | null) => {
    if (u) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u))
      localStorage.setItem('token', u.token)
    }
    setUserState(u)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('token')
    // 兼容旧版 mock 存储
    localStorage.removeItem('adminLoggedIn')
    localStorage.removeItem('adminAccount')
    localStorage.removeItem('medicalLoggedIn')
    localStorage.removeItem('medicalAccount')
    localStorage.removeItem('userLoggedIn')
    localStorage.removeItem('userAccount')
    setUserState(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        syncUser,
        isAdmin: user?.role === 'admin',
        isMedical: user?.role === 'medical',
        isUser: user?.role === 'user',
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// ============ Hook ============

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/**
 * 路由守卫：要求已登录 + 正确角色
 * 用法：在 App.tsx 的路由组件中包裹
 */
export function RequireAuth({
  children,
  role,
  redirectTo,
}: {
  children: ReactNode
  role?: AuthUser['role']
  redirectTo?: string
}) {
  const { user } = useAuth()
  
  if (!user) {
    const loginPath = redirectTo || (role === 'admin' ? '/admin/login' : role === 'medical' ? '/medical/login' : '/user/login')
    window.location.replace(loginPath)
    return null
  }

  if (role && user.role !== role) {
    window.location.replace(redirectTo || '/')
    return null
  }

  return <>{children}</>
}
